import { SensorStatus } from "@sitescene/types";
import { useState } from "react";

import { LoadingIndicator } from "@/components/loading-indicator/loading-indicator";
import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";
import { SensorsStatusModal } from "@/features/sensors/components/modals/sensors-status-modal";
import { useSensorSummary } from "@/features/sensors/hooks/useSensorSummary";
import { cx } from "@/utils/cx";

export const SensorStatusCard = () => {
	const [isSensorStatusModalOpen, setIsSensorStatusModalOpen] = useState(false);
	const [defaultModalStatus, setDefaultModalStatus] =
		useState<null | SensorStatus>(null);
	const {
		isLoadingSensorSummary,
		isSensorSummaryError,
		isSensorSummarySuccess,
		sensorSummaryData,
	} = useSensorSummary({
		refetchInterval: 20000,
	}); // 20 seconds

	const openModalWithStatus = (status: null | SensorStatus) => {
		setDefaultModalStatus(status);
		setIsSensorStatusModalOpen(true);
	};

	return (
		<>
			<div
				className={cx(
					"relative flex h-full flex-col justify-between overflow-hidden rounded-xl border border-secondary bg-primary p-3 text-primary",
					(isLoadingSensorSummary || isSensorSummaryError) &&
						"items-center justify-center",
				)}
			>
				{/* TODO: Replace with StatusMessage component when a smaller version is available */}
				{isSensorSummaryError && (
					<p className="text-center text-sm text-utility-red-500">
						Error loading sensor status. <br /> Please try again later.
					</p>
				)}
				{isLoadingSensorSummary && <LoadingIndicator size="sm" />}
				{isSensorSummarySuccess && (
					<>
						<div className="mb-4 flex items-center justify-between gap-2">
							<p className="text-sm font-semibold">Sensors Status</p>
							<Button
								color="link-color"
								onClick={() => openModalWithStatus(null)}
								size="sm"
							>
								View Details
							</Button>
						</div>

						<div className="flex flex-row gap-2">
							<Badge
								className="flex-1 justify-center"
								color={
									(sensorSummaryData?.online ?? 0) > 0 ? "success" : "gray"
								}
								onClick={() => openModalWithStatus(SensorStatus.Active)}
								type="color"
							>
								{sensorSummaryData?.online ?? 0} Online
							</Badge>
							<Badge
								className="flex-1 justify-center"
								color={
									(sensorSummaryData?.warning ?? 0) > 0 ? "warning" : "gray"
								}
								onClick={() => openModalWithStatus(SensorStatus.Warning)}
								type="color"
							>
								{/* Visual text shrinks to "Warn" at narrow widths; hide it from */}
								{/* screen readers and expose the full word via sr-only so the */}
								{/* accessible name is always "{count} Warning". */}
								<span aria-hidden="true">
									{sensorSummaryData?.warning ?? 0} Warn
									<span className="hidden 2xl:inline">ing</span>
								</span>
								<span className="sr-only">
									{sensorSummaryData?.warning ?? 0} Warning
								</span>
							</Badge>
							<Badge
								className="flex-1 justify-center"
								color={(sensorSummaryData?.offline ?? 0) > 0 ? "error" : "gray"}
								onClick={() => openModalWithStatus(SensorStatus.Offline)}
								type="color"
							>
								{sensorSummaryData?.offline ?? 0} Offline
							</Badge>
						</div>
					</>
				)}
			</div>

			<SensorsStatusModal
				defaultFilter={defaultModalStatus}
				isOpen={isSensorStatusModalOpen}
				offlineCount={sensorSummaryData?.offline ?? 0}
				onClose={() => setIsSensorStatusModalOpen(false)}
				onSelectedStatusChange={setDefaultModalStatus}
				selectedStatus={defaultModalStatus}
				source="sidebar"
			/>
		</>
	);
};
